<template>
    <div class="schedule-list-page" v-wechat-title="`空间课表_双优云桥-智慧研修平台`">
        <div class="schedule-wrap">
            <div class="crumb-bar">
                <a href="javascript:;" @click.prevent="goHome">项目首页</a>
                <span class="sep">/</span>
                <span class="cur">空间课表</span>
            </div>

            <div class="panel">
                <div class="panel-hd">
                    <h2>本周课表总览</h2>
                    <div class="toolbar">
                        <el-dropdown
                            trigger="click"
                            placement="bottom"
                            @command="onExportScheduleCommand"
                        >
                            <button
                                type="button"
                                class="export-btn"
                                :disabled="exportScheduleLoading || exportScheduleZipLoading"
                            >
                                <span class="export-btn__txt">导出课表</span>
                                <i
                                    v-if="!(exportScheduleLoading || exportScheduleZipLoading)"
                                    class="el-icon-arrow-down export-btn__icon"
                                ></i>
                                <i
                                    v-else
                                    class="el-icon-loading export-btn__icon"
                                ></i>
                            </button>
                            <el-dropdown-menu slot="dropdown" class="schedule-export-dropdown">
                                <el-dropdown-item
                                    command="excel"
                                    :disabled="exportScheduleLoading || exportScheduleZipLoading"
                                >Excel</el-dropdown-item>
                                <el-dropdown-item
                                    command="zip"
                                    :disabled="exportScheduleLoading || exportScheduleZipLoading"
                                >Word</el-dropdown-item>
                            </el-dropdown-menu>
                        </el-dropdown>
                    </div>
                </div>

                <div class="schedule-tabs-wrap" v-if="spaceTabs.length">
                    <button
                        type="button"
                        class="schedule-tabs-nav prev"
                        :class="{ 'is-disabled': !spaceTabsCanScrollLeft }"
                        :disabled="!spaceTabsCanScrollLeft"
                        @click="scrollSpaceTabs(-1)"
                    >
                        <i class="el-icon-arrow-left"></i>
                    </button>
                    <div class="schedule-tabs-scroll" ref="spaceTabsScroll" @scroll="updateSpaceTabsScrollState">
                        <div class="schedule-tabs-btns">
                            <button
                                v-for="tab in spaceTabs"
                                :key="tab.key"
                                type="button"
                                class="filter-tab"
                                :class="{ 'is-active': activeSpace === tab.key }"
                                :title="tab.name"
                                @click="selectSpaceTab(tab.key)"
                            >{{ tab.name }}</button>
                        </div>
                    </div>
                    <button
                        type="button"
                        class="schedule-tabs-nav next"
                        :class="{ 'is-disabled': !spaceTabsCanScrollRight }"
                        :disabled="!spaceTabsCanScrollRight"
                        @click="scrollSpaceTabs(1)"
                    >
                        <i class="el-icon-arrow-right"></i>
                    </button>
                </div>
                <div class="schedule-tabs-empty" v-else-if="!spaceTabsLoading">
                    暂无校级空间
                </div>

                <div class="schedule-toolbar">
                    <div class="week-toolbar-left">
                        <span class="week-chip-label">本周课表</span>
                        <span class="week-this-tag" v-if="weekOffset === 0">本周</span>
                    </div>
                    <div class="week-toolbar-right">
                        <button
                            type="button"
                            class="week-nav"
                            :disabled="!canSchedulePrev"
                            aria-label="上一周"
                            title="上一周"
                            @click="shiftWeek(-1)"
                        >
                            <i class="el-icon-arrow-left"></i>
                        </button>
                        <span class="week-chip-range">{{ weekLabel }}</span>
                        <button
                            type="button"
                            class="week-nav"
                            :disabled="!canScheduleNext"
                            aria-label="下一周"
                            title="下一周"
                            @click="shiftWeek(1)"
                        >
                            <i class="el-icon-arrow-right"></i>
                        </button>
                    </div>
                </div>

                <div class="space-block" v-loading="scheduleLoading">
                    <div class="space-block-hd" v-if="currentSpaceName">
                        <i class="el-icon-office-building"></i>
                        <span>{{ currentSpaceName }}</span>
                    </div>
                    <div class="schedule-body">
                        <div class="schedule-grid">
                            <div
                                v-for="day in currentWeekDays"
                                :key="day.key"
                                class="schedule-day"
                                :class="{ 'is-weekend': day.isWeekend, 'has-event': day.events.length }"
                            >
                                <div class="schedule-day-head">
                                    <span class="day-label">{{ day.label }}</span>
                                    <span class="day-num">{{ day.dayNum }}</span>
                                </div>
                                <div class="schedule-day-body">
                                    <template v-if="day.events && day.events.length">
                                        <div
                                            v-for="(ev, idx) in day.events"
                                            :key="idx"
                                            class="schedule-event"
                                            :class="ev.typeClass"
                                        >
                                            <div class="ev-top">
                                                <span class="ev-badge">{{ eventTypeLabel(ev.typeClass) }}</span>
                                                <span class="ev-time"><i class="el-icon-time"></i>{{ eventTimeOnly(ev.time) }}</span>
                                            </div>
                                            <div class="ev-title" :title="ev.title">{{ ev.title }}</div>
                                            <div class="ev-meta" v-if="ev.className">
                                                <span><i class="el-icon-user"></i>{{ ev.className }}</span>
                                            </div>
                                        </div>
                                    </template>
                                    <ph-empty
                                        v-else
                                        size="compact"
                                        text="暂无课程"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="schedule-legend">
                    <span><i class="lg-online"></i>直播课</span>
                    <span><i class="lg-live"></i>录播课</span>
                    <span><i class="lg-offline"></i>线下活动</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import PhEmpty from './components/ph-empty.vue'
import {
    getWeekLabel,
    computeWeekOffsetBounds,
    clampWeekOffset,
    buildWeekDayEvents,
    fetchSpaceWeekSchedule,
    fetchProjectSchedulePeriod as requestProjectSchedulePeriod,
    pickWeekOffsetWithData
} from './schedule-data'

export default {
    name: 'ProjectHomeScheduleList',
    components: { PhEmpty },
    data() {
        return {
            activeSpace: '',
            weekOffset: 0,
            spaceTabsLoading: false,
            spaceTabs: [],
            spaceTabsCanScrollLeft: false,
            spaceTabsCanScrollRight: false,
            scheduleLoading: false,
            scheduleWeekDays: [],
            projectSchedulePeriod: null,
            scheduleFetchSeq: 0,
            exportScheduleLoading: false,
            exportScheduleZipLoading: false
        }
    },
    computed: {
        ...mapState({
            currentProject: state => state.user.currentProject,
            userInfo: state => state.user.userInfo
        }),
        projectId() {
            return this.$route.params.id
        },
        weekLabel() {
            return getWeekLabel(this.weekOffset)
        },
        scheduleOffsetBounds() {
            return computeWeekOffsetBounds(this.projectSchedulePeriod)
        },
        canSchedulePrev() {
            const b = this.scheduleOffsetBounds
            if (!b) return true
            return this.weekOffset > b.minOffset
        },
        canScheduleNext() {
            const b = this.scheduleOffsetBounds
            if (!b) return true
            return this.weekOffset < b.maxOffset
        },
        currentSpaceName() {
            const tab = this.spaceTabs.find(item => item.key === this.activeSpace)
            return tab ? tab.name : ''
        },
        currentWeekDays() {
            if (this.scheduleWeekDays.length) return this.scheduleWeekDays
            return buildWeekDayEvents({
                weekOffset: this.weekOffset,
                onlineMap: {},
                offlineMap: {},
                publishedMap: {},
                resourceMap: {}
            })
        }
    },
    methods: {
        goHome() {
            this.$router.push(`/projectHome/${this.projectId}`)
        },
        eventTypeLabel(typeClass) {
            if (typeClass === 'ev-live') return '录播'
            if (typeClass === 'ev-offline') return '线下'
            return '直播'
        },
        eventTimeOnly(time) {
            if (!time) return ''
            return String(time).split('·')[0].trim()
        },
        async downloadScheduleBlob(blob, { failMsg, fileName, successMsg }) {
            if (!(blob instanceof Blob)) {
                this.$message.error(failMsg)
                return false
            }
            const mime = blob.type || ''
            if (mime.includes('json') || (mime.includes('text') && blob.size < 4096)) {
                try {
                    const text = await blob.text()
                    const json = JSON.parse(text)
                    if (json.code !== 200 && json.code !== '200') {
                        this.$message.error(json.msg || failMsg)
                        return false
                    }
                } catch (e) {
                    this.$message.error(failMsg)
                    return false
                }
            }
            const blobUrl = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = blobUrl
            a.download = fileName
            a.style.display = 'none'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(blobUrl)
            this.$message.success(successMsg)
            return true
        },
        onExportScheduleCommand(command) {
            if (command === 'excel') {
                this.exportScheduleExcel()
                return
            }
            if (command === 'zip') {
                this.exportScheduleWordZip()
            }
        },
        async exportScheduleExcel() {
            const spaceId = this.activeSpace
            if (!this.projectId || !spaceId) {
                this.$message.warning('请先选择空间')
                return
            }
            if (this.exportScheduleLoading) return
            this.exportScheduleLoading = true
            try {
                const blob = await this.$Api.Paired.getExportScheduleExcel({
                    projectId: this.projectId,
                    spaceId
                })
                const spaceName = this.currentSpaceName || '空间'
                await this.downloadScheduleBlob(blob, {
                    failMsg: '导出课表失败',
                    fileName: `${spaceName}结对空间课程表.xlsx`,
                    successMsg: '课表导出成功'
                })
            } catch (e) {
                this.$message.error('导出课表失败')
            } finally {
                this.exportScheduleLoading = false
            }
        },
        async exportScheduleWordZip() {
            const spaceId = this.activeSpace
            if (!this.projectId || !spaceId) {
                this.$message.warning('请先选择空间')
                return
            }
            if (this.exportScheduleZipLoading) return
            this.exportScheduleZipLoading = true
            try {
                const blob = await this.$Api.Paired.getExportScheduleWordZip({
                    projectId: this.projectId,
                    spaceIds: spaceId
                })
                const spaceName = this.currentSpaceName || '空间'
                await this.downloadScheduleBlob(blob, {
                    failMsg: '导出课表包失败',
                    fileName: `${spaceName}结对空间课程表包.zip`,
                    successMsg: '课表包导出成功'
                })
            } catch (e) {
                this.$message.error('导出课表包失败')
            } finally {
                this.exportScheduleZipLoading = false
            }
        },
        shiftWeek(delta) {
            const next = clampWeekOffset(this.weekOffset + delta, this.scheduleOffsetBounds)
            if (next === this.weekOffset) return
            this.weekOffset = next
            this.fetchSpaceSchedule({ allowAutoJump: false })
        },
        selectSpaceTab(key) {
            if (this.activeSpace === key) return
            this.activeSpace = key
            this.$nextTick(() => this.scrollActiveSpaceTabIntoView())
            this.fetchSpaceSchedule({ allowAutoJump: false })
        },
        updateSpaceTabsScrollState() {
            const el = this.$refs.spaceTabsScroll
            if (!el) {
                this.spaceTabsCanScrollLeft = false
                this.spaceTabsCanScrollRight = false
                return
            }
            const maxScroll = el.scrollWidth - el.clientWidth
            this.spaceTabsCanScrollLeft = el.scrollLeft > 2
            this.spaceTabsCanScrollRight = maxScroll - el.scrollLeft > 2
        },
        scrollSpaceTabs(direction) {
            const el = this.$refs.spaceTabsScroll
            if (!el) return
            const step = Math.max(Math.floor(el.clientWidth * 0.6), 160)
            el.scrollBy({ left: direction * step, behavior: 'smooth' })
        },
        scrollActiveSpaceTabIntoView() {
            const wrap = this.$refs.spaceTabsScroll
            if (!wrap) return
            const active = wrap.querySelector('.filter-tab.is-active')
            if (active && typeof active.scrollIntoView === 'function') {
                active.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' })
            }
            this.updateSpaceTabsScrollState()
        },
        async loadProjectSchedulePeriod() {
            if (!this.projectId) {
                this.projectSchedulePeriod = null
                return
            }
            this.projectSchedulePeriod = await requestProjectSchedulePeriod(this.$Api.Paired, this.projectId)
            const next = clampWeekOffset(this.weekOffset, this.scheduleOffsetBounds)
            if (next !== this.weekOffset) this.weekOffset = next
        },
        async fetchSpaceSchedule(options = {}) {
            const allowAutoJump = options.allowAutoJump === true
            const spaceId = this.activeSpace
            const userId = this.userInfo && this.userInfo.id
            if (!this.projectId || !spaceId) {
                this.scheduleWeekDays = buildWeekDayEvents({
                    weekOffset: this.weekOffset,
                    onlineMap: {},
                    offlineMap: {},
                    publishedMap: {},
                    resourceMap: {}
                })
                return
            }
            const seq = ++this.scheduleFetchSeq
            this.scheduleLoading = true
            try {
                const data = await fetchSpaceWeekSchedule(this.$Api.Paired, {
                    projectId: this.projectId,
                    spaceId,
                    userId,
                    weekOffset: this.weekOffset
                })
                if (seq !== this.scheduleFetchSeq) return
                let useData = data
                if (allowAutoJump) {
                    const nextOffset = pickWeekOffsetWithData({
                        onlineMap: data.onlineMap,
                        offlineMap: data.offlineMap,
                        publishedMap: data.publishedMap,
                        bounds: this.scheduleOffsetBounds,
                        currentOffset: this.weekOffset
                    })
                    if (nextOffset !== this.weekOffset) {
                        this.weekOffset = nextOffset
                        useData = await fetchSpaceWeekSchedule(this.$Api.Paired, {
                            projectId: this.projectId,
                            spaceId,
                            userId,
                            weekOffset: this.weekOffset
                        })
                        if (seq !== this.scheduleFetchSeq) return
                    }
                }
                this.scheduleWeekDays = buildWeekDayEvents({
                    weekOffset: this.weekOffset,
                    onlineMap: useData.onlineMap,
                    offlineMap: useData.offlineMap,
                    publishedMap: useData.publishedMap,
                    resourceMap: useData.resourceMap
                })
            } catch (e) {
                if (seq !== this.scheduleFetchSeq) return
                this.scheduleWeekDays = buildWeekDayEvents({
                    weekOffset: this.weekOffset,
                    onlineMap: {},
                    offlineMap: {},
                    publishedMap: {},
                    resourceMap: {}
                })
            } finally {
                if (seq === this.scheduleFetchSeq) this.scheduleLoading = false
            }
        },
        async fetchSpaceTabs() {
            if (!this.projectId) {
                this.spaceTabs = []
                this.activeSpace = ''
                this.scheduleWeekDays = []
                return
            }
            this.spaceTabsLoading = true
            try {
                const res = await this.$Api.Paired.getProjectSchoolSpacesAll({ projectId: this.projectId })
                const raw = (res && res.data) || []
                const records = Array.isArray(raw) ? raw : (raw.records || [])
                this.spaceTabs = records.map(item => {
                    const id = item.spaceId ?? item.id
                    return {
                        key: String(id),
                        id,
                        name: item.spaceName || item.spxName || '未命名空间'
                    }
                })
                if (!this.spaceTabs.find(tab => tab.key === this.activeSpace)) {
                    this.activeSpace = this.spaceTabs.length ? this.spaceTabs[0].key : ''
                }
                this.$nextTick(() => {
                    this.updateSpaceTabsScrollState()
                    this.scrollActiveSpaceTabIntoView()
                })
                await this.fetchSpaceSchedule({ allowAutoJump: false })
            } catch (e) {
                this.spaceTabs = []
                this.activeSpace = ''
                this.scheduleWeekDays = []
            } finally {
                this.spaceTabsLoading = false
            }
        }
    },
    mounted() {
        this.weekOffset = 0
        this.loadProjectSchedulePeriod().then(() => this.fetchSpaceTabs())
        this._onSpaceTabsResize = () => this.updateSpaceTabsScrollState()
        window.addEventListener('resize', this._onSpaceTabsResize)
    },
    beforeDestroy() {
        if (this._onSpaceTabsResize) {
            window.removeEventListener('resize', this._onSpaceTabsResize)
        }
    },
    watch: {
        '$route.params.id'() {
            this.weekOffset = 0
            this.loadProjectSchedulePeriod().then(() => this.fetchSpaceTabs())
        }
    }
}
</script>

<style lang="less" scoped>
.schedule-list-page {
    background: #f5f7fa;
    min-height: 60vh;
    padding: 24px 0 48px;
    line-height: 1.5;
}

.schedule-wrap {
    width: 990px;
    max-width: 100%;
    margin: 0 auto;
    box-sizing: border-box;
}

.crumb-bar {
    font-size: 13px;
    color: #999;
    margin-bottom: 16px;

    a {
        color: #01A2FD;
        text-decoration: none;
    }

    .sep {
        margin: 0 8px;
        color: #ccc;
    }

    .cur {
        color: #666;
    }
}

.export-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 34px;
    padding: 0 14px;
    border: none;
    border-radius: 4px;
    background: #01A2FD;
    color: #fff;
    font-size: 13px;
    font-family: inherit;
    line-height: 1;
    cursor: pointer;
    box-shadow: 0 3px 10px rgba(1, 162, 253, 0.28);
    transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 6px 16px rgba(1, 162, 253, 0.36);
        filter: brightness(1.03);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
        box-shadow: 0 2px 8px rgba(1, 162, 253, 0.24);
    }

    &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }

    .export-btn__txt {
        color: #fff;
    }

    .export-btn__icon {
        margin: 0;
        font-size: 12px;
        color: #fff;
    }
}

.panel {
    background: #fff;
    border: 1px solid rgba(199, 199, 199, 0.4);
    border-radius: 4px;
    overflow: hidden;
}

.panel-hd {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 0 20px;
    line-height: 56px;
    border-bottom: 1px solid #f0f3f7;
    background: rgba(250, 251, 255, 1);

    h2 {
        margin: 0;
        font-size: 16px;
        font-weight: 400;
        color: #333;
    }
}

.toolbar {
    display: flex;
    align-items: center;
}

.schedule-tabs-wrap {
    display: flex;
    align-items: stretch;
    border-bottom: 1px solid #f0f3f7;
    background: #fafbfd;
    overflow: hidden;
}

.schedule-tabs-nav {
    flex-shrink: 0;
    width: 32px;
    border: none;
    background: #fafbfd;
    color: #01A2FD;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &.is-disabled {
        color: #c5cdd8;
        cursor: default;
    }
}

.schedule-tabs-scroll {
    flex: 1;
    min-width: 0;
    overflow: hidden;
}

.schedule-tabs-btns {
    display: flex;
    flex-wrap: nowrap;
    width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        display: none;
    }
}

.filter-tab {
    flex: 0 0 140px;
    width: 140px;
    max-width: 140px;
    box-sizing: border-box;
    padding: 14px 10px;
    border: none;
    background: transparent;
    color: #666;
    font-size: 13px;
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
    position: relative;
    font-family: inherit;

    &.is-active {
        color: #01A2FD;

        &::after {
            content: "";
            position: absolute;
            left: 12px;
            right: 12px;
            bottom: 0;
            height: 2px;
            background: #01A2FD;
            border-radius: 1px;
        }
    }
}

.schedule-tabs-empty {
    padding: 12px 20px;
    font-size: 13px;
    color: #999;
    background: #fafbfd;
    border-bottom: 1px solid #f0f3f7;
}

.schedule-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 20px 6px;
}

.week-toolbar-left {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.week-toolbar-right {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.week-this-tag {
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 8px;
    border-radius: 4px;
    background: #1769FA;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
}

.week-nav {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #01A2FD;
    border: none;
    background: transparent;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: none;

    i {
        font-size: 14px;
        font-weight: 600;
    }

    &:hover {
        color: #1769FA;
        background: rgba(1, 162, 253, 0.08);
    }

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
        background: transparent;
    }
}

.week-chip-label {
    font-size: 14px;
    color: #333;
    font-weight: 600;
    line-height: 1.2;
}

.week-chip-range {
    font-size: 13px;
    color: #666;
    font-weight: 400;
    letter-spacing: 0.2px;
    white-space: nowrap;
    min-width: 120px;
    text-align: center;
}

.space-block {
    padding: 8px 16px 16px;
    min-height: 220px;
}

.space-block-hd {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    color: #333;
    margin-bottom: 12px;
    padding: 0 8px;

    i {
        color: #01A2FD;
    }
}

.schedule-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 10px;
}

.schedule-day {
    border: 1px solid #eef1f6;
    border-radius: 6px;
    background: #fff;
    min-height: 180px;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    &.is-weekend {
        background: #fafbfd;
    }
}

.schedule-day-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 10px 8px;
    border-bottom: 1px solid #f0f3f7;
}

.day-label {
    font-size: 13px;
    color: #666;
}

.day-num {
    font-size: 14px;
    color: #333;
}

.schedule-day-body {
    flex: 1;
    min-height: 0;
    max-height: 440px;
    padding: 10px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    scrollbar-color: rgba(1, 162, 253, 0.35) transparent;

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
        margin: 4px 0;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(1, 162, 253, 0.28);
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: rgba(1, 162, 253, 0.5);
    }
}

.schedule-event {
    padding: 8px;
    border-radius: 4px;
    background: #EAF6FF;
    border-left: 3px solid #01A2FD;
    flex-shrink: 0;
    overflow: hidden;
    box-sizing: border-box;

    &.ev-live {
        background: #eefaf3;
        border-left-color: #36cfc9;

        .ev-badge {
            color: #13a38a;
            background: rgba(19, 163, 138, 0.12);
        }
    }

    &.ev-offline {
        background: #fff7e8;
        border-left-color: #f5a623;

        .ev-badge {
            color: #d48806;
            background: rgba(212, 136, 6, 0.12);
        }
    }

    .ev-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 6px;
        margin-bottom: 6px;
    }

    .ev-badge {
        display: inline-block;
        padding: 1px 6px;
        margin-bottom: 0;
        border-radius: 3px;
        font-size: 11px;
        line-height: 16px;
        color: #01A2FD;
        background: rgba(1, 162, 253, 0.12);
        flex-shrink: 0;
    }

    .ev-time {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        font-size: 11px;
        color: #666;
        white-space: nowrap;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;

        i {
            font-size: 12px;
            color: #01A2FD;
        }
    }

    .ev-title {
        font-size: 12px;
        color: #333;
        line-height: 1.4;
        margin-bottom: 4px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .ev-meta {
        display: flex;
        flex-direction: column;
        gap: 2px;
        font-size: 11px;
        color: #999;

        i {
            margin-right: 2px;
            color: #01A2FD;
        }
    }
}

.schedule-legend {
    display: flex;
    gap: 18px;
    padding: 8px 20px 18px;
    font-size: 12px;
    color: #666;

    i {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 2px;
        margin-right: 6px;
        vertical-align: -1px;
    }

    .lg-online {
        background: #01A2FD;
    }

    .lg-live {
        background: #36cfc9;
    }

    .lg-offline {
        background: #f5a623;
    }
}

@media screen and (min-width: 1250px) {
    .schedule-wrap {
        width: 1200px;
    }
}

@media (max-width: 960px) {
    .schedule-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}
</style>

<style lang="less">
/* 下拉挂到 body，需非 scoped */
.schedule-export-dropdown.el-dropdown-menu {
    min-width: 96px;
    padding: 6px 0;
    margin-top: 8px !important;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 4px;
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.1);

    .el-dropdown-menu__item {
        padding: 8px 20px;
        font-size: 13px;
        line-height: 1.4;
        color: #606266;
        text-align: center;

        &:hover,
        &:focus {
            background: rgba(1, 162, 253, 0.08);
            color: #01A2FD;
        }

        &.is-disabled {
            color: #c0c4cc;
            background: transparent;
            cursor: not-allowed;
        }
    }

    .popper__arrow {
        border-bottom-color: rgba(0, 0, 0, 0.06) !important;

        &::after {
            border-bottom-color: #fff !important;
        }
    }
}
</style>
